import { useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { Link } from "react-router";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { Stepper } from "@mantine/core";
import {
  DEFAULT_LOAN_TERM_MONTHS,
  legalPersonKindSchema,
  type LoanAgreement,
  type ServiceAgreement,
} from "@/contracts";

import {
  createLoanAgreementMutation,
  deviceOptionsQuery,
  siteOptionsQuery,
} from "@/api/queries";
import { useAuth } from "@/auth/auth-context";
import { Alert } from "@/components/ui/alert";
import { Button } from "@/components/ui/button";
import { SelectField, TextField } from "@/components/ui/field";
import { PageHeader } from "@/components/ui/page";
import { LEGAL_PERSON_LABELS } from "@/features/loans/loan-labels";
import {
  ServiceAgreementCreateDialog,
  type ServiceAgreementPrefill,
} from "@/features/services/service-agreement-create-dialog";
import { SiteCreateDialog } from "@/features/sites/site-create-dialog";
import { useApiClient } from "@/lib/api-context";
import { formatDevEui } from "@/lib/format";

type Step = "site" | "devices" | "comodato" | "service" | "done";
/** Circles for the four content steps; `done` is Stepper.Completed. */
const WIZARD_STEPS = ["site", "devices", "comodato", "service"] as const;

function stepToActive(step: Step): number {
  if (step === "done") {
    return WIZARD_STEPS.length;
  }
  return WIZARD_STEPS.indexOf(step);
}

const loanFormSchema = z.object({
  borrowerKind: legalPersonKindSchema,
  borrowerName: z.string().trim().min(2).max(200),
  borrowerTaxId: z.string().trim().min(1).max(40),
  borrowerAddress: z.string().trim().min(5).max(300),
  signatoryName: z.string().trim().min(2).max(200),
  signatoryDocument: z.string().trim().min(1).max(40),
  signatoryCapacity: z.string().trim().min(2).max(120),
  purpose: z.string().trim().min(5).max(500),
  termMonths: z
    .string()
    .trim()
    .refine(
      (value) => Number.isInteger(Number(value)) && Number(value) >= 1 && Number(value) <= 120,
      { message: "Between 1 and 120 months." },
    ),
  startsOn: z.string().min(1),
});

type LoanFormValues = z.infer<typeof loanFormSchema>;

/**
 * Guided path for a new customer: site → devices → comodato draft →
 * service draft → links to finish Issue / PDF / attest on each instrument.
 */
export function OnboardingPage() {
  const { t } = useTranslation("pages");
  const { t: tCommon } = useTranslation("common");
  const { t: tLoans } = useTranslation("loans");
  const client = useApiClient();
  const { user } = useAuth();
  const queryClient = useQueryClient();

  const [step, setStep] = useState<Step>("site");
  const [siteId, setSiteId] = useState("");
  const [siteOrganizationId, setSiteOrganizationId] = useState("");
  const [siteName, setSiteName] = useState("");
  const [deviceIds, setDeviceIds] = useState<string[]>([]);
  const [loan, setLoan] = useState<LoanAgreement | null>(null);
  const [service, setService] = useState<ServiceAgreement | null>(null);
  const [creatingSite, setCreatingSite] = useState(false);
  const [drawingService, setDrawingService] = useState(false);

  const sites = useQuery(siteOptionsQuery(client));
  const devices = useQuery({
    ...deviceOptionsQuery(client, siteId),
    enabled: siteId !== "",
  });

  const selectedSite = useMemo(
    () => (sites.data?.data ?? []).find((site) => site.id === siteId),
    [sites.data, siteId],
  );

  function selectSite(id: string, organizationId: string, name: string) {
    setSiteId(id);
    setSiteOrganizationId(organizationId);
    setSiteName(name);
    setDeviceIds([]);
    setLoan(null);
    setService(null);
  }

  const {
    register,
    handleSubmit,
    control,
    setValue,
    formState: { errors, isSubmitting },
  } = useForm<LoanFormValues>({
    resolver: zodResolver(loanFormSchema),
    defaultValues: {
      borrowerKind: "LEGAL_ENTITY",
      borrowerName: "",
      borrowerTaxId: "",
      borrowerAddress: "",
      signatoryName: "",
      signatoryDocument: "",
      signatoryCapacity: "",
      purpose: "Monitoreo de temperatura",
      termMonths: String(DEFAULT_LOAN_TERM_MONTHS),
      startsOn: new Date().toISOString().slice(0, 10),
    },
  });

  const borrowerKind = useWatch({ control, name: "borrowerKind" });

  const createLoan = useMutation({
    ...createLoanAgreementMutation(client),
    onSuccess: async (created) => {
      await queryClient.invalidateQueries({ queryKey: ["loan-agreements"] });
      setLoan(created);
      setStep("service");
    },
  });

  const active = stepToActive(step);

  function allowStepClick(targetIndex: number): boolean {
    if (targetIndex >= WIZARD_STEPS.length) {
      return step === "done";
    }
    const target = WIZARD_STEPS[targetIndex];
    if (target === "site") {
      return true;
    }
    if (!siteId) {
      return false;
    }
    if (target === "devices") {
      return true;
    }
    if (target === "comodato") {
      return deviceIds.length > 0 || loan !== null;
    }
    if (target === "service") {
      return loan !== null;
    }
    return false;
  }

  function toggleDevice(id: string) {
    setDeviceIds((current) =>
      current.includes(id) ? current.filter((item) => item !== id) : [...current, id],
    );
  }

  const onCreateLoan = handleSubmit(async (values) => {
    if (!siteId || deviceIds.length === 0) {
      return;
    }

    await createLoan.mutateAsync({
      ...(user?.role === "SUPER_ADMIN" && siteOrganizationId
        ? { organizationId: siteOrganizationId }
        : {}),
      siteId,
      deviceIds,
      borrowerKind: values.borrowerKind,
      borrowerName: values.borrowerName,
      borrowerTaxId: values.borrowerTaxId,
      borrowerAddress: values.borrowerAddress,
      signatoryName: values.signatoryName,
      signatoryDocument: values.signatoryDocument,
      signatoryCapacity: values.signatoryCapacity,
      purpose: values.purpose,
      termMonths: Number(values.termMonths),
      startsOn: values.startsOn,
    });
  });

  const servicePrefill: ServiceAgreementPrefill | undefined =
    loan && loan.siteId
      ? {
          organizationId: loan.organizationId,
          siteId: loan.siteId,
          siteName: loan.siteName,
          loanAgreementId: loan.id,
          loanAgreementNumber: loan.number,
          customerKind: loan.borrowerKind,
          customerName: loan.borrowerName,
          customerTaxId: loan.borrowerTaxId,
          customerAddress: loan.borrowerAddress,
          signatoryName: loan.signatoryName,
          signatoryDocument: loan.signatoryDocument,
          signatoryCapacity: loan.signatoryCapacity,
        }
      : undefined;

  return (
    <>
      <PageHeader title={t("onboardingTitle")} description={t("onboardingDescription")} />

      <Stepper
        active={active}
        onStepClick={(index) => {
          if (!allowStepClick(index)) {
            return;
          }
          if (index >= WIZARD_STEPS.length) {
            setStep("done");
            return;
          }
          const next = WIZARD_STEPS[index];
          if (next) {
            setStep(next);
          }
        }}
        allowNextStepsSelect={false}
        color="brand"
        size="sm"
        labelPosition="bottom"
        aria-label={t("onboardingStepsLabel")}
        className="rounded-xl border border-border bg-surface p-4 sm:p-6"
      >
        <Stepper.Step
          label={t("onboardingStep.site")}
          description={siteName || selectedSite?.name || undefined}
          allowStepSelect={allowStepClick(0)}
        >
          <div className="mt-4 flex flex-col gap-4">
            <p className="text-sm text-ink-muted">{t("onboardingSiteHint")}</p>
            <SelectField
              label={tCommon("site")}
              required
              value={siteId}
              onChange={(value) => {
                const match = (sites.data?.data ?? []).find((site) => site.id === value);
                if (!match) {
                  selectSite("", "", "");
                  return;
                }
                selectSite(match.id, match.organizationId, match.name);
              }}
              data={[
                { value: "", label: t("onboardingChooseSite") },
                ...(sites.data?.data ?? []).map((site) => ({
                  value: site.id,
                  label: `${site.name}${site.address ? ` — ${site.address}` : ""}`,
                })),
                ...(siteId &&
                !(sites.data?.data ?? []).some((site) => site.id === siteId)
                  ? [{ value: siteId, label: siteName || siteId }]
                  : []),
              ]}
            />
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setCreatingSite(true)}>
                {t("onboardingNewSite")}
              </Button>
              <Button disabled={!siteId} onClick={() => setStep("devices")}>
                {tCommon("next")}
              </Button>
            </div>
          </div>
        </Stepper.Step>

        <Stepper.Step
          label={t("onboardingStep.devices")}
          description={
            deviceIds.length > 0
              ? t("onboardingComodatoDevices", { count: deviceIds.length })
              : undefined
          }
          allowStepSelect={allowStepClick(1)}
        >
          <div className="mt-4 flex flex-col gap-4">
            <p className="text-sm text-ink-muted">{t("onboardingDevicesHint")}</p>
            <p className="text-sm">
              <Link
                to="/devices/new"
                className="text-brand underline underline-offset-2"
              >
                {t("onboardingRegisterDevice")}
              </Link>
              <span className="text-ink-muted"> — {t("onboardingRegisterDeviceNote")}</span>
            </p>

            {devices.isPending ? (
              <p className="text-sm text-ink-muted">{t("onboardingLoadingDevices")}</p>
            ) : null}

            {devices.isError ? <Alert>{devices.error.message}</Alert> : null}

            {!devices.isPending && !devices.isError ? (
              (devices.data?.data ?? []).length === 0 ? (
                <p className="text-sm text-ink-muted">{t("onboardingNoDevices")}</p>
              ) : (
                <fieldset className="flex flex-col gap-2">
                  <legend className="mb-1 text-xs font-semibold text-ink-muted">
                    {t("onboardingSelectDevices")}
                  </legend>
                  {(devices.data?.data ?? []).map((device) => (
                    <label key={device.id} className="flex items-center gap-2 text-sm">
                      <input
                        type="checkbox"
                        className="size-4 rounded border-border accent-brand"
                        checked={deviceIds.includes(device.id)}
                        onChange={() => toggleDevice(device.id)}
                      />
                      <span className="font-mono text-xs">{formatDevEui(device.devEui)}</span>
                      <span className="text-ink-muted">
                        {device.manufacturer} {device.model}
                      </span>
                    </label>
                  ))}
                </fieldset>
              )
            ) : null}

            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setStep("site")}>
                {tCommon("previous")}
              </Button>
              <Button
                disabled={deviceIds.length === 0}
                onClick={() => setStep("comodato")}
              >
                {tCommon("next")}
              </Button>
            </div>
          </div>
        </Stepper.Step>

        <Stepper.Step
          label={t("onboardingStep.comodato")}
          description={loan?.number}
          allowStepSelect={allowStepClick(2)}
        >
          <div className="mt-4 flex flex-col gap-4">
            <p className="text-sm text-ink-muted">{t("onboardingComodatoHint")}</p>
            <p className="text-sm text-ink">
              {t("onboardingComodatoDevices", { count: deviceIds.length })}
            </p>

            {createLoan.isError ? <Alert>{createLoan.error.message}</Alert> : null}

            <fieldset className="flex flex-col gap-3 rounded-lg border border-border-subtle p-3">
              <legend className="px-1 text-xs font-semibold text-ink-muted">
                {tLoans("legendBorrower")}
              </legend>
              <SelectField
                label={tLoans("fieldBorrowerIs")}
                value={borrowerKind}
                onChange={(value) =>
                  setValue("borrowerKind", value as LoanFormValues["borrowerKind"], {
                    shouldValidate: true,
                    shouldDirty: true,
                  })
                }
                data={Object.entries(LEGAL_PERSON_LABELS).map(([value, label]) => ({
                  value,
                  label,
                }))}
              />
              <TextField
                label={tLoans("fieldLegalName")}
                required
                {...(errors.borrowerName?.message
                  ? { error: errors.borrowerName.message }
                  : {})}
                {...register("borrowerName")}
              />
              <TextField
                label={tLoans("fieldCuitCuil")}
                required
                {...(errors.borrowerTaxId?.message
                  ? { error: errors.borrowerTaxId.message }
                  : {})}
                {...register("borrowerTaxId")}
              />
              <TextField
                label={tLoans("fieldAddress")}
                required
                {...(errors.borrowerAddress?.message
                  ? { error: errors.borrowerAddress.message }
                  : {})}
                {...register("borrowerAddress")}
              />
            </fieldset>

            <fieldset className="flex flex-col gap-3 rounded-lg border border-border-subtle p-3">
              <legend className="px-1 text-xs font-semibold text-ink-muted">
                {tLoans("legendWhoSigns")}
              </legend>
              <TextField
                label={tLoans("fieldSignerName")}
                required
                {...(errors.signatoryName?.message
                  ? { error: errors.signatoryName.message }
                  : {})}
                {...register("signatoryName")}
              />
              <TextField
                label={tLoans("fieldIdDocument")}
                required
                {...(errors.signatoryDocument?.message
                  ? { error: errors.signatoryDocument.message }
                  : {})}
                {...register("signatoryDocument")}
              />
              <TextField
                label={tLoans("fieldCapacity")}
                hint={tLoans("hintCapacity")}
                required
                {...(errors.signatoryCapacity?.message
                  ? { error: errors.signatoryCapacity.message }
                  : {})}
                {...register("signatoryCapacity")}
              />
            </fieldset>

            <TextField
              label={tLoans("fieldPurpose")}
              required
              {...(errors.purpose?.message ? { error: errors.purpose.message } : {})}
              {...register("purpose")}
            />
            <div className="grid gap-3 sm:grid-cols-2">
              <TextField
                label={tLoans("fieldStartsOn")}
                type="date"
                required
                {...(errors.startsOn?.message ? { error: errors.startsOn.message } : {})}
                {...register("startsOn")}
              />
              <TextField
                label={tLoans("fieldTermMonths")}
                type="number"
                min={1}
                max={120}
                required
                {...(errors.termMonths?.message ? { error: errors.termMonths.message } : {})}
                {...register("termMonths")}
              />
            </div>

            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setStep("devices")}>
                {tCommon("previous")}
              </Button>
              <Button
                loading={isSubmitting || createLoan.isPending}
                onClick={() => void onCreateLoan()}
              >
                {t("onboardingCreateComodato")}
              </Button>
            </div>
          </div>
        </Stepper.Step>

        <Stepper.Step
          label={t("onboardingStep.service")}
          description={service?.number}
          allowStepSelect={allowStepClick(3)}
        >
          <div className="mt-4 flex flex-col gap-4">
            <p className="text-sm text-ink-muted">{t("onboardingServiceHint")}</p>
            {loan ? (
              <p className="text-sm text-ink">
                {t("onboardingServiceLinkedLoan", { number: loan.number })}
              </p>
            ) : null}
            <div className="flex flex-wrap gap-2">
              <Button variant="secondary" onClick={() => setStep("comodato")}>
                {tCommon("previous")}
              </Button>
              <Button
                disabled={!servicePrefill}
                onClick={() => setDrawingService(true)}
              >
                {t("onboardingCreateService")}
              </Button>
              <Button variant="ghost" onClick={() => setStep("done")}>
                {t("onboardingSkipService")}
              </Button>
            </div>
          </div>
        </Stepper.Step>

        <Stepper.Completed>
          <div className="mt-4 flex flex-col gap-4">
            <p className="text-sm text-ink-muted">{t("onboardingDoneHint")}</p>
            <ul className="list-inside list-disc text-sm">
              {siteId ? (
                <li>
                  <Link
                    to={`/sites/${siteId}`}
                    className="text-brand underline underline-offset-2"
                  >
                    {t("onboardingLinkSite", {
                      name: siteName || selectedSite?.name || siteId,
                    })}
                  </Link>
                </li>
              ) : null}
              {loan ? (
                <li>
                  <Link
                    to={`/loan-agreements/${loan.id}`}
                    className="text-brand underline underline-offset-2"
                  >
                    {t("onboardingLinkLoan", { number: loan.number })}
                  </Link>
                  <span className="text-ink-muted"> — {t("onboardingLinkLoanHint")}</span>
                </li>
              ) : null}
              {service ? (
                <li>
                  <Link
                    to={`/service-agreements/${service.id}`}
                    className="text-brand underline underline-offset-2"
                  >
                    {t("onboardingLinkService", { number: service.number })}
                  </Link>
                  <span className="text-ink-muted"> — {t("onboardingLinkServiceHint")}</span>
                </li>
              ) : (
                <li className="text-ink-muted">{t("onboardingNoServiceYet")}</li>
              )}
            </ul>
            <Button
              variant="secondary"
              onClick={() => {
                setStep("site");
                selectSite("", "", "");
              }}
            >
              {t("onboardingStartAnother")}
            </Button>
          </div>
        </Stepper.Completed>
      </Stepper>

      <SiteCreateDialog
        open={creatingSite}
        onClose={() => setCreatingSite(false)}
        onCreated={(created) => {
          selectSite(created.id, created.organizationId, created.name);
          void queryClient.invalidateQueries({ queryKey: ["sites"] });
        }}
      />

      {drawingService && servicePrefill ? (
        <ServiceAgreementCreateDialog
          open
          prefill={servicePrefill}
          onClose={() => setDrawingService(false)}
          onCreated={(created) => {
            setService(created);
            setStep("done");
          }}
        />
      ) : null}
    </>
  );
}
